import { zodResolver } from '@hookform/resolvers/zod';
import { registerSchema } from '@fitness/core';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/Button';
import { ErrorBanner } from '@/components/ErrorBanner';
import { Field, fieldAria } from '@/components/Field';
import { Input } from '@/components/Input';
import { SocialButtons } from '@/components/SocialButtons';
import { useAuth } from '@/lib/auth';
import { applyFieldErrors, explainError } from '@/lib/errors';
import { HOME_ROUTE } from '@/lib/redirect';

export const Route = createFileRoute('/register')({
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'authenticated') {
      throw redirect({ href: HOME_ROUTE });
    }
  },
  component: RegisterPage,
});

function RegisterPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<{ message: string; canRetry: boolean } | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: { email: '', password: '', displayName: '', handle: '', inviteCode: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await auth.register(values);
      await navigate({ href: HOME_ROUTE, replace: true });
    } catch (error) {
      // Los conflictos (correo o usuario ya cogidos, invitación) y los datos
      // que el servidor rechaza se muestran en el campo que los provoca.
      const explained = explainError(error);
      const fields = ['displayName', 'handle', 'email', 'password', 'inviteCode'] as const;
      if (applyFieldErrors(explained, fields, setError)) return;
      setFormError({ message: explained.message, canRetry: explained.canRetry });
    }
  });

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-app flex-col justify-center gap-6 p-6">
      <header className="text-center">
        <img src="/icons/icon-192.png" alt="" className="mx-auto size-16 rounded-2xl" />
        <h1 className="mt-4 text-2xl font-bold">Crea tu cuenta</h1>
        <p className="mt-1 text-sm text-ink-muted">Tardas menos de un minuto.</p>
      </header>

      {/* Con un proveedor, el código de invitación se pide después, en /auth/social. */}
      <SocialButtons redirect={HOME_ROUTE} />

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError ? (
          <ErrorBanner
            title="No se ha podido crear la cuenta"
            message={formError.message}
            {...(formError.canRetry ? { onRetry: () => void onSubmit() } : {})}
          />
        ) : null}

        <Field htmlFor="displayName" label="Nombre" error={errors.displayName?.message}>
          <Input
            {...fieldAria('displayName', errors.displayName?.message)}
            type="text"
            autoComplete="name"
            placeholder="Cómo quieres que te llamemos"
            {...register('displayName')}
          />
        </Field>

        <Field
          htmlFor="handle"
          label="Nombre de usuario"
          error={errors.handle?.message}
          hint="Así te encontrarán en la comunidad: minúsculas, dígitos y guion bajo."
        >
          <Input
            {...fieldAria('handle', errors.handle?.message, true)}
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="tu_usuario"
            {...register('handle')}
          />
        </Field>

        <Field htmlFor="email" label="Correo electrónico" error={errors.email?.message}>
          <Input
            {...fieldAria('email', errors.email?.message)}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="tu@correo.com"
            {...register('email')}
          />
        </Field>

        <Field
          htmlFor="password"
          label="Contraseña"
          error={errors.password?.message}
          hint="Mínimo 8 caracteres."
        >
          <Input
            {...fieldAria('password', errors.password?.message, true)}
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            {...register('password')}
          />
        </Field>

        <Field
          htmlFor="inviteCode"
          label="Código de invitación"
          error={errors.inviteCode?.message}
          hint="Opcional. Si te han invitado, pega aquí el código que te han pasado."
        >
          <Input
            {...fieldAria('inviteCode', errors.inviteCode?.message, true)}
            type="text"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            {...register('inviteCode')}
          />
        </Field>

        <Button type="submit" size="lg" block loading={isSubmitting}>
          Crear cuenta
        </Button>
      </form>

      <p className="text-center text-sm text-ink-muted">
        ¿Ya tienes cuenta?{' '}
        <Link to="/login" className="font-semibold text-brand underline-offset-4 hover:underline">
          Inicia sesión
        </Link>
      </p>
    </main>
  );
}
